import { useEffect, useReducer, useRef } from 'react';
import { NetworkStatus } from '@apollo/client';

import { DEFAULT_PAGINATION_INFO, DEFAULT_SHORT_PAGINATION, TARIFFS } from 'application/pages/constants';
import { CompanyUtils } from '@entities/company';
import { ItemShortType } from 'application/components/SelectableList/types';
import { useAccountCompanyId, useAccountCompanyIds, useAccountSelectors } from 'application/hooks';
import { useAppSelector } from '@shared/store';

import { useUserFeaturesOnboardingPass, useUserFeaturesOnboarding } from '@entities/media';
import {
  AcceptanceAdvanceStatus,
  PaymentKind,
  PointQueryFilter,
  PointQueryPagination,
  PointQuerySort,
  UserFeatures,
} from '@shared/api/graphql/generated';
import {
  useStreamSetToPoints,
  useStreamCopyTemplate,
  useStreamsSaved,
  useStream,
  usePointChangeStream,
  useStreamWithThematic,
} from '@entities/stream';
import { useBillingInfo, useAcceptanceAdvanced } from '@entities/billing';
import { usePointList } from '@entities/point';

import { DEFAULT_FILTERS, DEFAULT_STREAM_SET_POINTS_INPUT_STATE } from './consts';
import { streamSetPointsInputReducer } from './reducers';
import { StreamSetPointsInputActionTypes, UseStreamBindModalViewModel } from './types';

const useStreamBindModalViewModel = (): UseStreamBindModalViewModel => {
  const selectors = useAccountSelectors();

  const companyId = useAccountCompanyId();
  const companyIds = useAccountCompanyIds();

  const pricePlan = useAppSelector(selectors.selectCompanyPricePlan);

  const { loading: isChangeStreamLoading } = usePointChangeStream();
  const streamCopyTemplate = useStreamCopyTemplate();
  const savedStreams = useStreamsSaved();
  const targetStreamData = useStream();
  const { readStreamWithThematic, clearStreamWithThematic } = useStreamWithThematic();

  const isCopyStreamTemplateLoading = streamCopyTemplate.loading;

  const copyTemplateStream = async (templateId: string, companyId: string, withThematic: boolean = false) => {
    const result = await streamCopyTemplate.request(templateId, companyId, withThematic);
    return result;
  };

  const getSavedStreams = async (companyIds: string[]) => {
    const result = await savedStreams.request({ companyIds });
    return result;
  };

  const getStream = async (id: string) => {
    const result = await targetStreamData.request(id);
    return result;
  };

  const { selectedPaymentKind, hasTrialLicenses } = useBillingInfo(companyId);

  const { request: onboardingPassedRequest, result: onboardingPassedResult } = useUserFeaturesOnboardingPass();
  const {
    items: userOnboardingFeatures,
    refetch: refetchFeaturesOnboarding,
    loading: userOnboardingFeaturesLoading,
  } = useUserFeaturesOnboarding({
    features: [UserFeatures.WarningStreamFirstStart],
  });

  const { loading: acceptanceAdvancedLoading, acceptanceAdvanced } = useAcceptanceAdvanced(companyId);

  const [streamSetToPointsRequest, streamSetToPointsResult] = useStreamSetToPoints();
  const [requestPointList, pointListResult] = usePointList();

  const filtersRef = useRef<PointQueryFilter>({
    ...DEFAULT_FILTERS,
    companyIds,
  });

  const paginationRef = useRef<PointQueryPagination>(DEFAULT_SHORT_PAGINATION);

  const sortRef = useRef<PointQuerySort>(PointQuerySort.IdDesc);

  const [state, dispatch] = useReducer(streamSetPointsInputReducer, DEFAULT_STREAM_SET_POINTS_INPUT_STATE);

  const setPointInput = (needAdd: boolean, point: ItemShortType) => {
    dispatch({ type: StreamSetPointsInputActionTypes.SET_POINT, needAdd, point });
  };

  const requestPoints = async (
    filters: PointQueryFilter = DEFAULT_FILTERS,
    pagination: PointQueryPagination = DEFAULT_SHORT_PAGINATION
  ) => {
    filtersRef.current = {
      ...filtersRef.current,
      ...filters,
      companyIds,
    };

    paginationRef.current = {
      ...paginationRef.current,
      ...pagination,
    };

    await requestPointList(filtersRef.current, paginationRef.current, sortRef.current);
  };

  const loadMorePoints = async () => {
    const page = (pointListResult.data.paginationInfo?.page || 0) + 1;

    if (page <= paginationRef.current.page) return;

    paginationRef.current.page = page;

    await pointListResult.loadMore(filtersRef.current, paginationRef.current, sortRef.current);
  };

  const onFilterExecute = async (filters: PointQueryFilter) => {
    const preparedFilters: PointQueryFilter = {
      companyIds,
    };

    const { query } = filters;

    if (query !== undefined) {
      preparedFilters.query = query;
      filtersRef.current.query = query;
    }

    if (!preparedFilters.query && filtersRef.current.query) {
      preparedFilters.query = filtersRef.current.query;
    }

    await requestPointList(preparedFilters, paginationRef.current, sortRef.current);
  };

  const streamSetToPoints = async (streamId: string) => {
    const pointIds = state.filter && state.filter.pointIds ? [...state.filter.pointIds.keys()] : [];
    const isOk = await streamSetToPointsRequest({ companyId, streamId, pointIds });

    if (isOk) {
      dispatch({ type: StreamSetPointsInputActionTypes.RESET_STATE });
    }
  };

  const pointsLoading =
    pointListResult.networkStatus === NetworkStatus.loading ||
    pointListResult.networkStatus === NetworkStatus.setVariables;

  const pointsCalled = pointListResult.called;
  const points = pointListResult.data.points || [];

  const isLoading =
    isChangeStreamLoading ||
    isCopyStreamTemplateLoading ||
    onboardingPassedResult.loading ||
    userOnboardingFeaturesLoading ||
    pointsLoading;

  const isPopularTariff = CompanyUtils.compareTariff(pricePlan?.alias ?? '', TARIFFS.POPULAR);
  const isCustomInfoShouldShow = !userOnboardingFeatures[0]?.isOnboarded && isPopularTariff;

  const isReady = pointsCalled && (!pointsLoading || points.length !== 0);
  const isFilterQueryEmpty = !filtersRef.current.query;
  const isEmpty = isReady && points.length === 0 && isFilterQueryEmpty;
  const isEmptyFiltered = isReady && points.length === 0 && !isFilterQueryEmpty;

  const isAcceptanceAdvancedPending =
    selectedPaymentKind === PaymentKind.Sbbol &&
    (acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Pending || acceptanceAdvancedLoading) &&
    !hasTrialLicenses;

  useEffect(() => {
    requestPoints(filtersRef.current, paginationRef.current);
  }, []);

  return {
    streamSetPointsState: state,
    isReady,
    isEmpty,
    isEmptyFiltered,
    points,
    pointsLoading,
    paginationInfo: pointListResult.data.paginationInfo || DEFAULT_PAGINATION_INFO,
    allPointsCount: pointListResult.data.paginationInfo?.totalCount || points.length,
    isCustomInfoShouldShow,
    isLoading,
    userOnboardingFeaturesLoading,
    isAcceptanceAdvancedPending,
    onFilterExecute,
    loadMorePoints,
    streamSetToPoints,
    streamSetToPointsLoading: streamSetToPointsResult.loading,
    copyTemplateStream,
    getSavedStreams,
    getStream,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    readStreamWithThematic,
    clearStreamWithThematic,
    setPointInput,
  };
};

export default useStreamBindModalViewModel;
